import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import StaffingDestinationPageSchema, {
  StaffingDestinationPageJSON,
} from "work/staffing/_schemas/staffing-destination-page/staffing-destination-page-schema";
import { StaffingDestinationKind } from "work/staffing/_schemas/staffing-destination/staffing-destination-schema";
import StaffingOutcomeSchema, {
  StaffingOutcomeJSON,
} from "work/staffing/_schemas/staffing-outcome/staffing-outcome-schema";

/** Who the viewer is considering, as both halves of the seat a work team holds. */
export type StaffingCandidate = {
  userId: string;
  legalEntityId: string;
};

export type AddToStaffingDestinationArgs =
  | { kind: "Proposal"; destinationId: string; candidate: StaffingCandidate }
  | { kind: "WorkAgreement"; destinationId: string; candidate: StaffingCandidate; version: number };

function workAgreementVersionHeader(version: number): HeadersInit {
  if (!Number.isInteger(version) || version <= 0) {
    throw new Error("Cannot staff a work agreement without its current version.");
  }
  return { "If-Match": `"${version}"` };
}

/**
 * The columns the destinations endpoint will sort on.
 *
 * Two, and the same two the server declares. Both have to be orders the underlying stores can
 * honour: the endpoint reads a bounded window of each store, so an order it could only apply
 * afterwards would sort the window rather than the set. The counterparty is the column this rules
 * out — its name is resolved after the page is cut, so no store can order by it.
 */
export type StaffingDestinationOrderField = "Name" | "ActiveSince";

/** Which way a column is sorted, spelled as the API's `OrderDirection` is. */
export type StaffingOrderDirection = "Ascending" | "Descending";

export type StaffingDestinationQuery = {
  candidate?: StaffingCandidate;
  /** Matched against the work's name, in the store. */
  search?: string;
  /** Narrows to one kind of work. Both kinds when absent. */
  kind?: StaffingDestinationKind;
  /** Which column to sort on. The name when absent. */
  orderBy?: StaffingDestinationOrderField;
  /** Which way to sort it. Ascending when absent. */
  orderDir?: StaffingOrderDirection;
  pageIndex?: number;
  pageSize?: number;
};

/**
 * The query string the destinations endpoint is asked with.
 *
 * Built here rather than inline so the search term and the page travel as parameters the server
 * acts on. Narrowing a fetched page in the browser would find only what that page happened to
 * contain, which is the same defect at a different size.
 */
export function staffingDestinationsUrl(query: StaffingDestinationQuery): string {
  const params = new URLSearchParams();
  if (query.candidate) {
    params.set("candidateUserId", query.candidate.userId);
    params.set("candidateLegalEntityId", query.candidate.legalEntityId);
  }
  if (query.search && query.search.trim().length > 0) params.set("search", query.search.trim());
  if (query.kind) params.set("kind", query.kind);
  // Sorting goes to the server for the same reason paging does. Each store hands back a bounded
  // window, so the order is what decides which rows are inside it; reordering a fetched page would
  // sort ten rows out of two hundred and call it "oldest first".
  if (query.orderBy) params.set("orderBy", query.orderBy);
  if (query.orderDir) params.set("orderDir", query.orderDir);
  if (query.pageIndex !== undefined) params.set("pageIndex", `${query.pageIndex}`);
  if (query.pageSize !== undefined) params.set("pageSize", `${query.pageSize}`);
  const search = params.toString();
  return search.length > 0 ? `work/staffing-destinations?${search}` : "work/staffing-destinations";
}

export const workStaffingApi = api.injectEndpoints({
  endpoints: (build) => ({
    /**
     * One page of the work the signed-in viewer may put somebody onto, decided server-side.
     *
     * The candidate is who is being considered, and the answer says per destination whether they
     * already belong. It is not a filter the client could apply itself: the API answers with the
     * same authority that the add command asserts, so an empty answer is the honest reason the Add
     * to Work action does not appear at all.
     *
     * Tagged with both kinds of work it names, so seating somebody through one route and then
     * reopening a profile does not offer a stale answer.
     */
    getStaffingDestinations: build.query<StaffingDestinationPageJSON, StaffingDestinationQuery>({
      query: (arg) => staffingDestinationsUrl(arg),
      rawResponseSchema: StaffingDestinationPageSchema.StaffingDestinationPageJSONZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: [
        { type: "Proposal" as const, id: "LIST" },
        { type: "WorkAgreement" as const, id: "LIST" },
      ],
    }),

    /**
     * Seats one person on one piece of work.
     *
     * The roster is deliberately not part of the request. The server reads what the team has and
     * extends it, so a profile that never loaded the roster cannot drop anybody from it.
     */
    addToStaffingDestination: build.mutation<
      StaffingOutcomeJSON,
      AddToStaffingDestinationArgs
    >({
      query: (args) => ({
        url: "work/staffing-destinations/members",
        method: "POST",
        body: {
          kind: args.kind,
          destinationId: args.destinationId,
          candidateUserId: args.candidate.userId,
          candidateLegalEntityId: args.candidate.legalEntityId,
        },
        headers: {
          "Content-Type": "application/json",
          ...(args.kind === "WorkAgreement" ? workAgreementVersionHeader(args.version) : {}),
        },
      }),
      rawResponseSchema: StaffingOutcomeSchema.StaffingOutcomeJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, args) =>
        args.kind === "Proposal"
          ? [
              { type: "Proposal" as const, id: args.destinationId },
              { type: "Proposal" as const, id: "LIST" },
            ]
          : [
              { type: "WorkAgreement" as const, id: args.destinationId },
              { type: "WorkAgreement" as const, id: "LIST" },
              // A revision is a proposal of its own, and one may have just been opened.
              { type: "Proposal" as const, id: "LIST" },
            ],
    }),
  }),
});

export const { useGetStaffingDestinationsQuery, useAddToStaffingDestinationMutation } = workStaffingApi;
